import React from "react";
import { useTheme } from "../../../../Utils/useTheme";
import { Column } from "../types";
import { PivotRow } from "../engine/pivotEngine";
import { getColumnStyle } from "../utils/getColumnStyle";

const getValue = <T,>(row: T, col: Column<T>) => {
  if (col.accessor) return col.accessor(row);
  return (row as any)[col.key];
};

type Props = {
  columns: Column<any>[];
  rows: (any | PivotRow<any>)[];
  getRowKey: (row: any, index: number) => string;
  onToggleGroup?: (key: string) => void;
};

const SETTINGS_COL_W = 52;

export const TableBody = ({ columns, rows, getRowKey, onToggleGroup }: Props) => {
  const { border, text, hover } = useTheme();

  const renderRow = (row: any, i: number) => {
    const key = getRowKey(row, i);
    const zebra = i % 2 === 0 ? "rgba(17,24,39,0.02)" : "transparent";

    return (
      <tr
        key={key}
        style={{ background: zebra, color: text }}
        onMouseEnter={(e) => (((e.currentTarget as any).style.background = hover) as any)}
        onMouseLeave={(e) => (((e.currentTarget as any).style.background = zebra) as any)}
      >
        {columns.map((c) => {
          const raw = getValue(row, c);
          const cell = c.render ? c.render(row, raw) : raw;

          return (
            <td
              key={c.key}
              style={{
                ...getColumnStyle(c),
                padding: "0 12px",
                height: 44,
                borderBottom: `1px solid ${border}`,
                fontSize: 13,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                textAlign: c.align === "right" ? "right" : c.align === "center" ? "center" : "left",
                verticalAlign: "middle",
              }}
            >
              {cell == null || cell === "" ? <span style={{ opacity: 0.45 }}>—</span> : cell}
            </td>
          );
        })}

        <td
          style={{
            width: SETTINGS_COL_W,
            minWidth: SETTINGS_COL_W,
            maxWidth: SETTINGS_COL_W,
            borderBottom: `1px solid ${border}`,
          }}
        />
      </tr>
    );
  };

  const renderPivot = (p: PivotRow<any>, i: number) => {
    if (p.kind === "row") return renderRow(p.row, i);

    return (
      <tr key={`g:${p.group.key}`} onClick={() => onToggleGroup?.(p.group.key)} style={{ cursor: "pointer" }}>
        <td
          colSpan={columns.length + 1}
          style={{
            height: 44,
            borderBottom: `1px solid ${border}`,
            background: "rgba(17,24,39,0.03)",
            color: text,
            fontWeight: 900,
            padding: "0 12px",
            verticalAlign: "middle",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <span style={{ width: 18 }}>{p.group.collapsed ? "▸" : "▾"}</span>
            <span>{p.group.label}</span>
            <span
              style={{
                marginLeft: 8,
                padding: "2px 8px",
                borderRadius: 999,
                background: "rgba(17,24,39,0.08)",
                fontSize: 12,
              }}
            >
              {p.group.count}
            </span>
          </div>
        </td>
      </tr>
    );
  };

  return <tbody>{rows.map((r: any, i: number) => (r?.kind ? renderPivot(r, i) : renderRow(r, i)))}</tbody>;
};
